// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for retrieving an initial true-up close run by ID.
 * Provides type-safe initial true-up close run retrieval with Zod validation.
 */
import * as z from 'zod';
import { initialTrueUpCloseRunResponseSchema } from '@/accounting/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';

/**
 * Schema for validating the response from the get initial true-up close run API endpoint.
 * Includes both the close run data and metadata about the request.
 */
const getInitialTrueUpCloseRunResponseSchema = z
  .object({
    data: initialTrueUpCloseRunResponseSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    closeRun: data,
  }));

/**
 * Type definition for the get initial true-up close run API response.
 * Contains the close run data and request metadata.
 */
type GetInitialTrueUpCloseRunResponse = z.infer<
  typeof getInitialTrueUpCloseRunResponseSchema
>;

/**
 * Retrieves an initial true-up close run by its ID.
 *
 * @param id - The unique identifier of the initial true-up close run to retrieve
 * @returns Promise resolving to the initial true-up close run data and request metadata
 * @throws Will throw an error if the close run is not found, API request fails, or response validation fails
 */
export async function getInitialTrueUpCloseRun(
  id: string
): Promise<GetInitialTrueUpCloseRunResponse> {
  const url = `/v1/accounting/initial-true-ups/${id}`;
  const response = await api.get(url);

  return parseResponse(getInitialTrueUpCloseRunResponseSchema, response.data, {
    schemaName: 'InitialTrueUpCloseRun',
    endpoint: url,
  });
}
